import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminCurrentImagePreview,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  buildYowanPersonOptions,
  createYowanAdminInstructor,
  fetchYowanAdminMemberDetail,
  fetchYowanAdminMemberOptions,
  fetchYowanAdminPersonList,
  getYowanAdminMutationErrorMessage,
  resolveYowanAdminPersonId,
  validateYowanImageUploadFile,
  YOWAN_IMAGE_UPLOAD_HELP_TEXT,
} from '../app/yowanContentClient';
import AdminPersonLedgerSection from './AdminPersonLedgerSection';
const { yowanPageContent } = require('./pageContent');

const instructorMemberPlaceholderOptions = [{ value: '', label: '未紐づき / 外部講師' }];

const FieldNote = ({ children, style }) => {
  if (!children) {
    return null;
  }

  return (
    <p
      className="yowan-admin-form__field-note"
      style={style || undefined}
    >
      {children}
    </p>
  );
};

const AdminInstructorEditorFields = ({
  content,
  register,
  errors,
  memberLocked = false,
  lockedMemberLabel = '',
  currentImage,
}) => {
  const { labels, options, helpText, errors: formErrors } = content.form;
  const validation = content.form.validation || {};
  const biographyMaxLength = validation.biographyMaxLength || 220;
  const careerMaxLength = validation.careerMaxLength || 800;

  return (
    <>
      <div className="c-form__field">
        <span className="c-form__label">
          {labels.instructorName}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="text"
          {...register('instructorName', { required: formErrors.required })}
        />
        {errors.instructorName ? (
          <em className="yowan-admin-form__error">{errors.instructorName.message}</em>
        ) : null}
        <FieldNote>{helpText.instructorName}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">
          {labels.instructorEmail}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="email"
          {...register('instructorEmail', { required: formErrors.required })}
        />
        {errors.instructorEmail ? (
          <em className="yowan-admin-form__error">{errors.instructorEmail.message}</em>
        ) : null}
        <FieldNote>{helpText.instructorEmail}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.memberId}</span>
        {memberLocked ? (
          <>
            <input type="hidden" {...register('memberId')} />
            <input
              className="c-form__control"
              type="text"
              value={lockedMemberLabel || '選択済みの会員'}
              disabled
              readOnly
            />
          </>
        ) : (
          <select className="c-form__control" {...register('memberId')}>
            {(options.members || []).map((option) => (
              <option key={option.value || 'empty'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
        <FieldNote>{helpText.memberId}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">
          {labels.roleLabel}
          <em className="c-form__required">*</em>
        </span>
        <input
          className="c-form__control"
          type="text"
          {...register('roleLabel', { required: formErrors.required })}
        />
        {errors.roleLabel ? (
          <em className="yowan-admin-form__error">{errors.roleLabel.message}</em>
        ) : null}
        <FieldNote>{helpText.roleLabel}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.specialties}</span>
        <input className="c-form__control" type="text" {...register('specialties')} />
      </div>

      <div className="c-form__field">
        <span className="c-form__label">
          {labels.biography}
          <em className="c-form__required">*</em>
        </span>
        <textarea
          className="c-form__control"
          rows="4"
          maxLength={biographyMaxLength}
          {...register('biography', {
            required: formErrors.required,
            maxLength: {
              value: biographyMaxLength,
              message: `${biographyMaxLength}文字以内で入力してください。`,
            },
          })}
        />
        {errors.biography ? (
          <em className="yowan-admin-form__error">{errors.biography.message}</em>
        ) : null}
        <FieldNote>{helpText.biography}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">
          {labels.career}
          <em className="c-form__required">*</em>
        </span>
        <textarea
          className="c-form__control"
          rows="6"
          maxLength={careerMaxLength}
          {...register('career', {
            required: formErrors.required,
            maxLength: {
              value: careerMaxLength,
              message: `${careerMaxLength}文字以内で入力してください。`,
            },
          })}
        />
        {errors.career ? (
          <em className="yowan-admin-form__error">{errors.career.message}</em>
        ) : null}
        <FieldNote>{helpText.career}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.image}</span>
        <input type="hidden" {...register('primary_image_id')} />
        <AdminCurrentImagePreview image={currentImage} />
        <input className="c-form__control" type="file" accept="image/*" {...register('image', { validate: validateYowanImageUploadFile })} />
        {errors.image ? (
          <em className="yowan-admin-form__error">{errors.image.message}</em>
        ) : null}
        <FieldNote>{[helpText.image, YOWAN_IMAGE_UPLOAD_HELP_TEXT].filter(Boolean).join(' ')}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.webLinkWebsite}</span>
        <input className="c-form__control" type="url" {...register('webLinkWebsite')} />
        <FieldNote>{helpText.webLinks}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.webLinkFacebook}</span>
        <input className="c-form__control" type="url" {...register('webLinkFacebook')} />
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.webLinkX}</span>
        <input className="c-form__control" type="url" {...register('webLinkX')} />
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.webLinkInstagram}</span>
        <input className="c-form__control" type="url" {...register('webLinkInstagram')} />
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.webLinkYoutube}</span>
        <input className="c-form__control" type="url" {...register('webLinkYoutube')} />
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.webLinkTiktok}</span>
        <input className="c-form__control" type="url" {...register('webLinkTiktok')} />
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.webLinkOther}</span>
        <input className="c-form__control" type="url" {...register('webLinkOther')} />
      </div>

      <div className="c-form__field yowan-admin-form__toggle-field">
        <span className="c-form__label">{labels.isActive}</span>
        <label className="c-form__choice yowan-admin-form__toggle-choice">
          <input type="checkbox" {...register('isActive')} />
          <span>{labels.isActive}</span>
        </label>
        <FieldNote>{helpText.isActive}</FieldNote>
      </div>

      <div className="c-form__field yowan-admin-form__toggle-field">
        <span className="c-form__label">{labels.publicProfileEnabled}</span>
        <label className="c-form__choice yowan-admin-form__toggle-choice">
          <input type="checkbox" {...register('publicProfileEnabled')} />
          <span>{labels.publicProfileEnabled}</span>
        </label>
        <FieldNote>{helpText.publicProfileEnabled}</FieldNote>
      </div>

      <div className="c-form__field">
        <span className="c-form__label">{labels.remarks}</span>
        <textarea className="c-form__control" rows="4" {...register('remarks')} />
      </div>
    </>
  );
};

const AdminInstructorsCreatePage = () => {
  const content = yowanPageContent['/admin/events/instructors/new'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const memberId = searchParams.get('memberId') || '';
  const memberLabel = searchParams.get('memberLabel') || '';
  const [lockedMemberLabel, setLockedMemberLabel] = useState(memberLabel);
  const [memberOptions, setMemberOptions] = useState(instructorMemberPlaceholderOptions);
  const [personOptions, setPersonOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isLoadingPersonOptions, setIsLoadingPersonOptions] = useState(false);
  const [optionLoadError, setOptionLoadError] = useState('');
  const [personOptionLoadError, setPersonOptionLoadError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formContent = {
    ...content,
    form: {
      ...content?.form,
      options: {
        ...content?.form?.options,
        members: memberId
          ? [{ value: memberId, label: lockedMemberLabel || '対象会員を読み込み中' }]
          : memberOptions,
      },
    },
  };
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...content?.form.defaults,
      memberId: memberId || content?.form.defaults?.memberId || '',
    },
  });

  useEffect(() => {
    if (memberLabel) {
      setLockedMemberLabel(memberLabel);
      return undefined;
    }

    if (!memberId) {
      return undefined;
    }

    let isMounted = true;

    fetchYowanAdminMemberDetail(memberId)
      .then((record) => {
        if (isMounted) {
          setLockedMemberLabel(`${record.name || '氏名未設定'} / ${record.email || '-'}`);
        }
      })
      .catch(() => {
        if (isMounted) {
          setLockedMemberLabel('選択済みの会員');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [memberId, memberLabel]);

  useEffect(() => {
    if (!content || memberId) {
      return undefined;
    }

    let isMounted = true;

    const loadMemberOptions = async () => {
      setIsLoadingOptions(true);
      setOptionLoadError('');

      try {
        const options = await fetchYowanAdminMemberOptions({
          profileKind: 'instructor',
        });

        if (isMounted) {
          setMemberOptions([
            { value: '', label: '未紐づき / 外部講師' },
            ...options,
          ]);
        }
      } catch (error) {
        if (isMounted) {
          setOptionLoadError('会員選択肢を読み込めませんでした。');
        }
      } finally {
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    };

    loadMemberOptions();

    return () => {
      isMounted = false;
    };
  }, [content, memberId]);

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    let isMounted = true;

    const loadPersonOptions = async () => {
      setIsLoadingPersonOptions(true);
      setPersonOptionLoadError('');

      try {
        const people = await fetchYowanAdminPersonList();

        if (isMounted) {
          setPersonOptions(buildYowanPersonOptions(people.results));
        }
      } catch (error) {
        if (isMounted) {
          setPersonOptionLoadError('人物台帳の選択肢を読み込めませんでした。');
        }
      } finally {
        if (isMounted) {
          setIsLoadingPersonOptions(false);
        }
      }
    };

    loadPersonOptions();

    return () => {
      isMounted = false;
    };
  }, [content]);

  if (!content) {
    return null;
  }

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      const personId = await resolveYowanAdminPersonId(values);

      await createYowanAdminInstructor({ ...values, personId });
      setSubmitMessage('保存しました。');
      navigate(appendYowanPreviewQueryIfNeeded('/admin/events/instructors', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref="/admin/events/instructors" routeKey="/admin/events/instructors/new">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/events/instructors', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />
      <AdminRelatedLinks links={content.relatedLinks} preservedDomainSearch={preservedDomainSearch} />

      <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        {isLoadingOptions ? <LoadingSpinner size="sm" /> : null}
        {optionLoadError ? <p className="yowan-admin-form__message">{optionLoadError}</p> : null}
        <AdminInstructorEditorFields
          content={formContent}
          register={register}
          errors={errors}
          memberLocked={Boolean(memberId)}
          lockedMemberLabel={lockedMemberLabel}
        />
        <AdminPersonLedgerSection
          content={formContent}
          register={register}
          errors={errors}
          watch={watch}
          setValue={setValue}
          personOptions={personOptions}
          isLoadingOptions={isLoadingPersonOptions}
          optionLoadError={personOptionLoadError}
        />

        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting}>
            {content.form.submitLabel}
          </button>
        </div>

        {isSubmitting ? (
          <p className="yowan-admin-form__message yowan-admin-form__message--pending">
            <LoadingSpinner inline size="sm" />
            <span>{content.form.pendingMessage}</span>
          </p>
        ) : submitMessage ? (
          <p className="yowan-admin-form__message">{submitMessage}</p>
        ) : null}
      </form>
    </AdminBlogShell>
  );
};

export { AdminInstructorEditorFields };
export default AdminInstructorsCreatePage;
